import { useQuery } from '@tanstack/react-query'
import { signalStrengthApi } from '../services/signalStrengthApi'

/**
 * Sigfox link-quality queries.
 *
 * Unlike the admin missing-readings hooks, these are NOT gated on isAdmin():
 * signal strength is exactly what a field installer needs, and the endpoints
 * are user-facing and ownership-scoped server-side. The `enabled` flag is used
 * only to avoid firing before an id is known.
 */

const STALE_MS = 60_000

/**
 * One device's recent samples plus a summary.
 *
 * `refetchIntervalMs` exists for the installation view: while someone is
 * standing at a site adjusting an antenna, the page should pick up the next
 * transmission on its own rather than needing a manual refresh. Off by default
 * so ordinary device viewing does not poll.
 */
export function useDeviceSignal(
  sigfoxId: string | undefined,
  days = 7,
  limit = 50,
  refetchIntervalMs?: number
) {
  return useQuery({
    queryKey: ['signal', 'device', sigfoxId, days, limit],
    queryFn: () => signalStrengthApi.getDeviceSignal(sigfoxId!, days, limit),
    enabled: !!sigfoxId,
    staleTime: STALE_MS,
    ...(refetchIntervalMs ? { refetchInterval: refetchIntervalMs } : {}),
  })
}

/**
 * Per-day signal over the window, for the device chart. Never polled: a day's
 * median does not move on the next transmission, so Live mode leaves it alone.
 */
export function useDeviceSignalDaily(sigfoxId: string | undefined, days: number) {
  return useQuery({
    queryKey: ['signal', 'device-daily', sigfoxId, days],
    queryFn: () => signalStrengthApi.getDeviceSignalDaily(sigfoxId!, days),
    enabled: !!sigfoxId,
    staleTime: STALE_MS,
  })
}

/**
 * Fleet roll-up for the dashboard. Omit `sigfoxIds` for "all my devices";
 * `dailyDays` adds each device's per-day series over that many days.
 */
export function useFleetSignal(sigfoxIds?: string[], days = 7, enabled = true, dailyDays?: number) {
  return useQuery({
    queryKey: ['signal', 'fleet', sigfoxIds?.join(',') ?? 'all', days, dailyDays ?? null],
    queryFn: () => signalStrengthApi.getFleetSignal(sigfoxIds, days, dailyDays),
    enabled,
    staleTime: STALE_MS,
  })
}
